import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Language, ProjectCategory } from '../types';

export const useAppStore = defineStore('app', () => {
  const language = ref<Language>('zh');
  const isMenuOpen = ref(false);
  const isScrolled = ref(false);
  const showBackToTop = ref(false);
  const activeSection = ref('home');
  const activeProjectFilter = ref<ProjectCategory>('all');
  const isLoading = ref(false);

  const isZh = computed(() => language.value === 'zh');

  function setLanguage(lang: Language) {
    language.value = lang;
    localStorage.setItem('preferred-language', lang);
  }

  function toggleMenu() {
    isMenuOpen.value = !isMenuOpen.value;
  }

  function closeMenu() {
    isMenuOpen.value = false;
  }

  function updateScrollState(scrollY: number) {
    isScrolled.value = scrollY > 50;
    showBackToTop.value = scrollY > 300;
  }

  function setActiveSection(section: string) {
    activeSection.value = section;
  }

  function setProjectFilter(filter: ProjectCategory) {
    activeProjectFilter.value = filter;
  }

  function initializeLanguage() {
    const saved = localStorage.getItem('preferred-language') as Language | null;
    if (saved) {
      language.value = saved;
    } else {
      const browserLang = navigator.language.toLowerCase();
      language.value = browserLang.startsWith('zh') ? 'zh' : 'en';
    }
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
    closeMenu();
  }

  return {
    language,
    isMenuOpen,
    isScrolled,
    showBackToTop,
    activeSection,
    activeProjectFilter,
    isLoading,
    isZh,
    setLanguage,
    toggleMenu,
    closeMenu,
    updateScrollState,
    setActiveSection,
    setProjectFilter,
    initializeLanguage,
    scrollToTop,
    scrollToSection
  };
});
